// client/src/pages/Admin/ProjectManager/services/projectService.ts
/**
 * PROJECT SERVICE
 * 
 * This file contains all API service functions for project operations.
 * It handles authentication, error handling, and data transformation.
 * 
 * @module ProjectManager/Services
 */

import {
    Project,
    CreateProjectDTO,
    UpdateProjectDTO,
    ProjectService as IProjectService
} from '../types/project.types';
import {
    API_ENDPOINTS,
    STORAGE_KEYS
} from '../constants/project.constants';

// ============================================
// AUTHENTICATION HELPERS
// ============================================

/**
 * Get authentication token from local storage
 * @returns JWT token or null
 */
const getToken = (): string | null => {
    try {
        return localStorage.getItem('adminToken');
    } catch {
        return null;
    }
};

/**
 * Handle unauthorized access
 */
const handleUnauthorized = (): void => {
    try {
        localStorage.removeItem('adminToken');
        window.location.href = '/admin';
    } catch {
        // If redirect fails, reload the page
        window.location.reload();
    }
};

/**
 * Create headers with authentication
 * @returns Headers object with Authorization
 */
const getAuthHeaders = (): HeadersInit => {
    const token = getToken();
    if (!token) {
        throw new Error('No authentication token found');
    }
    return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
    };
};

// ============================================
// ERROR HANDLING
// ============================================

/**
 * Custom error class for API errors
 */
export class ProjectAPIError extends Error {
    constructor(
        message: string,
        public status?: number,
        public data?: any
    ) {
        super(message);
        this.name = 'ProjectAPIError';
    }
}

/**
 * Handle API response and parse errors
 * @param response - Fetch response
 * @returns Parsed JSON data
 * @throws ProjectAPIError
 */
const handleResponse = async (response: Response): Promise<any> => {
    // Handle unauthorized
    if (response.status === 401) {
        handleUnauthorized();
        throw new ProjectAPIError('Session expired. Please login again.', 401);
    }

    // Handle other errors
    if (!response.ok) {
        let errorMessage = `API Error: ${response.status}`;
        let errorData = null;

        try {
            const data = await response.json();
            if (data.error) {
                errorMessage = data.error;
            }
            errorData = data;
        } catch {
            // If response is not JSON, use status text
            errorMessage = response.statusText || errorMessage;
        }

        throw new ProjectAPIError(errorMessage, response.status, errorData);
    }

    // Parse JSON response
    try {
        return await response.json();
    } catch {
        throw new ProjectAPIError('Invalid response format from server');
    }
};

// ============================================
// PROJECT SERVICE IMPLEMENTATION
// ============================================

export const projectService: IProjectService = {
    /**
     * Fetch all projects
     * @returns Promise with array of projects
     */
    async fetchProjects(): Promise<Project[]> {
        try {
            const headers = getAuthHeaders();
            const response = await fetch(API_ENDPOINTS.PROJECTS, {
                headers,
                method: 'GET'
            });

            const data = await handleResponse(response);
            return data.data || [];
        } catch (error) {
            console.error('Error fetching projects:', error);
            throw error;
        }
    },

    /**
     * Fetch a single project by ID
     * @param id - Project ID
     * @returns Promise with project
     */
    async getProject(id: string): Promise<Project> {
        if (!id) {
            throw new ProjectAPIError('Project ID is required');
        }

        try {
            const headers = getAuthHeaders();
            const response = await fetch(API_ENDPOINTS.PROJECT(id), {
                headers,
                method: 'GET'
            });

            const data = await handleResponse(response);
            return data.data;
        } catch (error) {
            console.error(`Error fetching project ${id}:`, error);
            throw error;
        }
    },

    /**
     * Create a new project
     * @param project - Project data
     * @returns Promise with created project
     */
    async createProject(project: CreateProjectDTO): Promise<Project> {
        // Validate required fields
        if (!project.title) {
            throw new ProjectAPIError('Project title is required');
        }
        if (!project.description) {
            throw new ProjectAPIError('Project description is required');
        }
        if (!project.category) {
            throw new ProjectAPIError('Project category is required');
        }

        // Validate date
        if (project.startDate && isNaN(new Date(project.startDate).getTime())) {
            throw new ProjectAPIError('Invalid start date');
        }
        if (project.endDate && isNaN(new Date(project.endDate).getTime())) {
            throw new ProjectAPIError('Invalid end date');
        }
        if (project.startDate && project.endDate) {
            const start = new Date(project.startDate);
            const end = new Date(project.endDate);
            if (end < start) {
                throw new ProjectAPIError('End date must be after start date');
            }
        }

        try {
            const headers = getAuthHeaders();
            const response = await fetch(API_ENDPOINTS.PROJECT_CREATE, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    ...project,
                    startDate: project.startDate?.toISOString(),
                    endDate: project.endDate?.toISOString()
                })
            });

            const data = await handleResponse(response);
            return data.data;
        } catch (error) {
            console.error('Error creating project:', error);
            throw error;
        }
    },

    /**
     * Update an existing project
     * @param id - Project ID
     * @param updates - Updates to apply
     * @returns Promise with updated project
     */
    async updateProject(id: string, updates: Partial<CreateProjectDTO>): Promise<Project> {
        if (!id) {
            throw new ProjectAPIError('Project ID is required');
        }

        // Validate date if provided
        if (updates.startDate && isNaN(new Date(updates.startDate).getTime())) {
            throw new ProjectAPIError('Invalid start date');
        }
        if (updates.endDate && isNaN(new Date(updates.endDate).getTime())) {
            throw new ProjectAPIError('Invalid end date');
        }
        if (updates.startDate && updates.endDate) {
            const start = new Date(updates.startDate);
            const end = new Date(updates.endDate);
            if (end < start) {
                throw new ProjectAPIError('End date must be after start date');
            }
        }

        try {
            const headers = getAuthHeaders();
            const response = await fetch(API_ENDPOINTS.PROJECT_UPDATE, {
                method: 'PUT',
                headers,
                body: JSON.stringify({
                    id,
                    ...updates,
                    startDate: updates.startDate?.toISOString(),
                    endDate: updates.endDate?.toISOString()
                })
            });

            const data = await handleResponse(response);
            return data.data;
        } catch (error) {
            console.error(`Error updating project ${id}:`, error);
            throw error;
        }
    },

    /**
     * Delete a project
     * @param id - Project ID
     * @returns Promise<void>
     */
    async deleteProject(id: string): Promise<void> {
        if (!id) {
            throw new ProjectAPIError('Project ID is required');
        }

        try {
            const headers = getAuthHeaders();
            const response = await fetch(API_ENDPOINTS.PROJECT_DELETE, {
                method: 'DELETE',
                headers,
                body: JSON.stringify({ id })
            });

            await handleResponse(response);
        } catch (error) {
            console.error(`Error deleting project ${id}:`, error);
            throw error;
        }
    },

    /**
     * Bulk update projects
     * @param ids - Array of project IDs
     * @param updates - Updates to apply
     * @returns Promise<void>
     */
    async bulkUpdateProjects(ids: string[], updates: Partial<Project>): Promise<void> {
        if (!ids || ids.length === 0) {
            throw new ProjectAPIError('At least one project ID is required');
        }

        try {
            const headers = getAuthHeaders();
            const response = await fetch(API_ENDPOINTS.PROJECT_BULK_UPDATE, {
                method: 'PATCH',
                headers,
                body: JSON.stringify({
                    ids,
                    ...updates,
                    updatedAt: new Date().toISOString()
                })
            });

            await handleResponse(response);
        } catch (error) {
            console.error('Error bulk updating projects:', error);
            throw error;
        }
    },

    /**
     * Bulk delete projects
     * @param ids - Array of project IDs
     * @returns Promise<void>
     */
    async bulkDeleteProjects(ids: string[]): Promise<void> {
        if (!ids || ids.length === 0) {
            throw new ProjectAPIError('At least one project ID is required');
        }

        try {
            const headers = getAuthHeaders();
            const response = await fetch(API_ENDPOINTS.PROJECT_BULK_DELETE, {
                method: 'DELETE',
                headers,
                body: JSON.stringify({ ids })
            });

            await handleResponse(response);
        } catch (error) {
            console.error('Error bulk deleting projects:', error);
            throw error;
        }
    }
};

// ============================================
// SERVICE CACHE HELPERS
// ============================================

/**
 * Cache storage for project data
 */
class ProjectCache {
    private cache: Map<string, { data: Project; timestamp: number }> = new Map();
    private readonly TTL = 5 * 60 * 1000; // 5 minutes

    set(id: string, data: Project): void {
        this.cache.set(id, { data, timestamp: Date.now() });
    }

    get(id: string): Project | null {
        const entry = this.cache.get(id);
        if (!entry) return null;
        if (Date.now() - entry.timestamp > this.TTL) {
            this.cache.delete(id);
            return null;
        }
        return entry.data;
    }

    clear(): void {
        this.cache.clear();
    }

    remove(id: string): void {
        this.cache.delete(id);
    }
}

export const projectCache = new ProjectCache();

// ============================================
// EXPORT HELPER FUNCTIONS
// ============================================

/**
 * Get project export data (for CSV/JSON)
 * @param projects - Array of projects
 * @returns Formatted export data
 */
export const getProjectExportData = (projects: Project[]): any[] => {
    if (!Array.isArray(projects)) return [];

    return projects.map(project => ({
        id: project._id,
        title: project.title,
        subtitle: project.subtitle || '',
        category: project.category,
        status: project.status,
        impact: project.impact,
        description: project.description,
        fullDescription: project.fullDescription || '',
        startDate: project.startDate,
        endDate: project.endDate || '',
        isOngoing: project.isOngoing,
        regions: (project.regions || []).join('; '),
        districts: (project.districts || []).join('; '),
        tags: (project.tags || []).join('; '),
        featured: project.featured,
        views: project.views || 0,
        createdAt: project.createdAt,
        updatedAt: project.updatedAt
    }));
};

/**
 * Save filter preferences to local storage
 * @param filters - Filter options
 */
export const saveFilterPreferences = (filters: any): void => {
    try {
        localStorage.setItem(STORAGE_KEYS.PROJECT_FILTERS, JSON.stringify(filters));
    } catch {
        // Silently fail if localStorage is not available
    }
};

/**
 * Load filter preferences from local storage
 * @returns Saved filter preferences or null
 */
export const loadFilterPreferences = (): any | null => {
    try {
        const data = localStorage.getItem(STORAGE_KEYS.PROJECT_FILTERS);
        return data ? JSON.parse(data) : null;
    } catch {
        return null;
    }
};

/**
 * Clear all project-related storage
 */
export const clearProjectStorage = (): void => {
    try {
        Object.values(STORAGE_KEYS).forEach(key => {
            localStorage.removeItem(key);
        });
        projectCache.clear();
    } catch {
        // Silently fail
    }
};